import { useState, useEffect } from 'react';

/**
 * Hook to detect if the current device is touch-primary
 * Uses touch capability AND coarse pointer detection to exclude
 * laptops with touchscreens that primarily use mouse
 * 
 * @returns {boolean} Whether the device is touch-primary
 */
export const useTouchDevice = () => {
    // Must start false to match SSR output
    const [isTouchDevice, setIsTouchDevice] = useState(false);
    
    useEffect(() => {
        if (typeof window === 'undefined') return;
        
        const checkTouchDevice = () => {
            // Check for touch capability
            const hasTouch = 'ontouchstart' in window || 
                             navigator.maxTouchPoints > 0 ||
                             navigator.msMaxTouchPoints > 0;
            
            // Also check if primary input is coarse (touch) vs fine (mouse)
            const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
            
            // Device is touch-primary if it has touch AND uses coarse pointer
            // This excludes laptops with touchscreens that primarily use mouse
            setIsTouchDevice(hasTouch && hasCoarsePointer);
        };
        
        // Initial check
        checkTouchDevice();
        
        // Listen for pointer type changes (e.g., connecting/disconnecting mouse)
        const pointerQuery = window.matchMedia('(pointer: coarse)');
        const handlePointerChange = () => checkTouchDevice();
        
        if (pointerQuery.addEventListener) {
            pointerQuery.addEventListener('change', handlePointerChange);
        } else {
            // Fallback for older browsers
            pointerQuery.addListener(handlePointerChange);
        }
        
        return () => {
            if (pointerQuery.removeEventListener) {
                pointerQuery.removeEventListener('change', handlePointerChange);
            } else {
                pointerQuery.removeListener(handlePointerChange);
            }
        };
    }, []);
    
    return isTouchDevice;
};

export default useTouchDevice;
